// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import type { Problem } from "./types";

/** The embedded app's API; nginx (and the dev proxy) maps it to granite-api /v1/shopify/. */
export const API_BASE = "/api";

/** An API failure: the server's RFC 9457 problem, or one made up for network and parse errors. */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string | undefined {
    return this.problem.code;
  }
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.title === "string" && typeof v.status === "number";
}

/** Reads a failed response into an ApiError, whether or not the body is a problem document. */
export async function problemFrom(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Not JSON (a proxy's HTML error page, an empty body): fall back to the status line.
  }
  if (isProblem(body)) {
    const p = body;
    return new ApiError({
      type: typeof p.type === "string" ? p.type : "about:blank",
      title: p.title,
      status: p.status,
      ...(typeof p.detail === "string" ? { detail: p.detail } : {}),
      ...(typeof p.code === "string" ? { code: p.code } : {}),
      ...(Array.isArray(p.errors) ? { errors: p.errors } : {}),
    });
  }
  return new ApiError({ type: "about:blank", title: res.statusText || `HTTP ${res.status}`, status: res.status });
}

async function send(path: string, init: RequestInit): Promise<Response> {
  // A fresh ID token per request: App Bridge caches it and renews it before it expires (1 min).
  const token = await window.shopify.idToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json, application/problem+json");
  if (init.body !== undefined && init.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

/**
 * Calls the embedded app API with the merchant's session token and returns the JSON body
 * (undefined for 204 and other empty bodies). A 401 is retried once with a new token, since a token can expire
 * between minting and arrival. Failures throw ApiError.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await send(path, init);
    if (res.status === 401) res = await send(path, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({ type: "about:blank", title: "network", status: 0, code: "network" });
  }
  if (!res.ok) throw await problemFrom(res);
  if (res.status === 204 || res.headers.get("Content-Length") === "0") return undefined as T;
  // A 202 Accepted (re-sync) may come without a body.
  const text = await res.text();
  return (text === "" ? undefined : JSON.parse(text)) as T;
}

/** POSTs a JSON body. */
export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
}
